/**
 * DCS-tab extractors. Pure functions, no RTK / no Neo4j driver.
 */

import { extractNode, extractNodeList, extractRawNode, toIntCount } from '../graph-row'
import { extractComputeRefs } from '../refs'
import { compareByNameThenId } from '../sort'
import { extractPlantMeta } from './provenance'
import type {
  CypherReadResult,
  PlantBlockParamGroup,
  PlantBlockType,
  PlantCee,
  PlantControlAlarm,
  PlantControlModule,
  PlantControlSetpoint,
  PlantControlTag,
  PlantDcsController,
  PlantDcsNodeDetail,
  PlantDcsNodeKind,
  PlantDcsTree,
  PlantDcsTreeNode,
  PlantDcsWiredPeer,
  PlantFunctionBlock,
  PlantIoChannel,
  PlantIoModule
} from '../types'

const TREE_KINDS: ReadonlySet<PlantDcsTreeNode['kind']> = new Set([
  'DcsController',
  'Cee',
  'ControlModule',
  'FunctionBlock',
  'IoModule',
  'IoChannel'
])

export function mapPlantDcsTree(raw: CypherReadResult): PlantDcsTree {
  const nodes: PlantDcsTreeNode[] = []
  const seen = new Set<string>()
  let totalControlTagCount = 0
  for (const row of raw.results) {
    totalControlTagCount = toIntCount(row['totalControlTagCount'])
    const node = extractRawNode(row['node'])
    if (!node) continue
    const kind = row['kind']
    if (typeof kind !== 'string' || !TREE_KINDS.has(kind as PlantDcsTreeNode['kind'])) continue
    const id = typeof node['id'] === 'string' ? node['id'] : (node['_id'] as string)
    if (!id || seen.has(id)) continue
    seen.add(id)
    const name = typeof node['name'] === 'string' ? node['name'] : id
    const parentId = typeof row['parentId'] === 'string' ? row['parentId'] : null
    nodes.push({
      id,
      parentId,
      kind: kind as PlantDcsTreeNode['kind'],
      name,
      ...extractPlantMeta(node),
      childCount: toIntCount(row['childCount'])
    })
  }
  return { nodes, totalControlTagCount }
}

function isStrayFor(row: Record<string, unknown>, kind: string): boolean {
  const strayLabels = Array.isArray(row['strayLabels'])
    ? row['strayLabels'].filter((label): label is string => typeof label === 'string')
    : []
  return strayLabels.includes(kind)
}

function optionalString(value: unknown): string | undefined {
  return typeof value === 'string' && value !== '' ? value : undefined
}

function optionalNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined
}

const WIRE_ROLES = new Set(['data', 'peer', 'associated', 'back_calculation'])

function wiredPeer(value: unknown): PlantDcsWiredPeer[] {
  if (!Array.isArray(value)) return []
  const out: PlantDcsWiredPeer[] = []
  for (const item of value) {
    if (!item || typeof item !== 'object') continue
    const obj = item as Record<string, unknown>
    const id = typeof obj['id'] === 'string' ? obj['id'] : ''
    const name = typeof obj['name'] === 'string' ? obj['name'] : id
    const kind = obj['kind'] === 'IoChannel' ? 'IoChannel' : 'FunctionBlock'
    if (!id) continue
    const role = optionalString(obj['role'])
    out.push({
      id,
      name,
      kind,
      from_param: optionalString(obj['from_param']),
      to_param: optionalString(obj['to_param']),
      from_index: optionalNumber(obj['from_index']),
      to_index: optionalNumber(obj['to_index']),
      ...(role && WIRE_ROLES.has(role) ? { role } : {}),
      conn_id: optionalNumber(obj['conn_id'])
    })
  }
  return out.sort(compareByNameThenId)
}

export type PlantDcsParamGroupBucket = {
  name: string
  properties: Array<{ key: string; value: string }>
}

/** Split extra instance properties onto Control Builder tabs from `BlockParamGroup.param_names`. */
export function bucketDcsParamGroups(
  properties: Array<{ key: string; value: string }>,
  groups: PlantBlockParamGroup[]
): { buckets: PlantDcsParamGroupBucket[]; remainder: Array<{ key: string; value: string }> } {
  const remaining = new Map(properties.map((prop) => [prop.key, prop]))
  const sorted = [...groups].sort((a, b) => {
    const an = typeof a.group_number === 'number' ? a.group_number : Number.MAX_SAFE_INTEGER
    const bn = typeof b.group_number === 'number' ? b.group_number : Number.MAX_SAFE_INTEGER
    return an - bn || a.name.localeCompare(b.name)
  })
  const buckets: PlantDcsParamGroupBucket[] = []
  for (const group of sorted) {
    const bucket: PlantDcsParamGroupBucket['properties'] = []
    for (const key of group.param_names ?? []) {
      const prop = remaining.get(key)
      if (!prop) continue
      bucket.push(prop)
      remaining.delete(key)
    }
    if (bucket.length > 0) buckets.push({ name: group.name, properties: bucket })
  }
  return { buckets, remainder: [...remaining.values()] }
}

export function mapPlantDcsNodeDetail(raw: CypherReadResult, nodeId: string): PlantDcsNodeDetail {
  const row = raw.results[0]
  if (!row) {
    throw new Error(`DCS node ${nodeId} not found`)
  }
  const labels = Array.isArray(row['labelList']) ? (row['labelList'] as unknown[]) : []

  if (labels.includes('DcsController')) {
    const controller = extractNode<PlantDcsController>(row['n'])
    if (!controller) throw new Error(`DcsController ${nodeId} could not be projected`)
    return {
      kind: 'DcsController',
      isStray: isStrayFor(row, 'DcsController'),
      controller,
      cees: extractNodeList<PlantCee>(row['controllerCees']).sort(compareByNameThenId),
      ioModules: extractNodeList<PlantIoModule>(row['controllerIoModules']).sort(
        compareByNameThenId
      ),
      commsBlocks: extractNodeList<PlantFunctionBlock>(row['controllerComms']).sort(
        compareByNameThenId
      ),
      partner: extractNode<PlantDcsController>(row['controllerPartner']),
      mappedComputes: extractComputeRefs(row['controllerMappedComputes'])
    }
  }
  if (labels.includes('Cee')) {
    const cee = extractNode<PlantCee>(row['n'])
    if (!cee) throw new Error(`Cee ${nodeId} could not be projected`)
    return {
      kind: 'Cee',
      isStray: isStrayFor(row, 'Cee'),
      cee,
      controller: extractNode<PlantDcsController>(row['ceeController']),
      modules: extractNodeList<PlantControlModule>(row['ceeModules']).sort(compareByNameThenId),
      commsBlocks: extractNodeList<PlantFunctionBlock>(row['ceeComms']).sort(compareByNameThenId)
    }
  }
  if (labels.includes('ControlModule')) {
    const module = extractNode<PlantControlModule>(row['n'])
    if (!module) throw new Error(`ControlModule ${nodeId} could not be projected`)
    return {
      kind: 'ControlModule',
      isStray: isStrayFor(row, 'ControlModule'),
      module,
      cee: extractNode<PlantCee>(row['moduleCee']),
      controller: extractNode<PlantDcsController>(row['moduleController']),
      functionBlocks: extractNodeList<PlantFunctionBlock>(row['moduleBlocks']).sort(
        compareByNameThenId
      ),
      sequenceBlocks: extractNodeList<PlantFunctionBlock>(row['moduleSequence']).sort(
        compareByNameThenId
      ),
      tags: extractNodeList<PlantControlTag>(row['moduleTags']).sort(compareByNameThenId)
    }
  }
  if (labels.includes('FunctionBlock')) {
    const block = extractNode<PlantFunctionBlock>(row['n'])
    if (!block) throw new Error(`FunctionBlock ${nodeId} could not be projected`)
    return {
      kind: 'FunctionBlock',
      isStray: isStrayFor(row, 'FunctionBlock'),
      block,
      module: extractNode<PlantControlModule>(row['blockModule']),
      parentBlock: extractNode<PlantFunctionBlock>(row['blockParent']),
      parentCee: extractNode<PlantCee>(row['blockCee']),
      parentController: extractNode<PlantDcsController>(row['blockController']),
      childBlocks: extractNodeList<PlantFunctionBlock>(row['blockChildren']).sort(
        compareByNameThenId
      ),
      blockType: extractNode<PlantBlockType>(row['blockType']),
      derivedFrom: extractNode<PlantBlockType>(row['blockDerivedFrom']),
      paramGroups: extractNodeList<PlantBlockParamGroup>(row['blockParamGroups']).sort(
        (a, b) =>
          (a.group_number ?? Number.MAX_SAFE_INTEGER) -
            (b.group_number ?? Number.MAX_SAFE_INTEGER) || a.name.localeCompare(b.name)
      ),
      wiredTo: wiredPeer(row['blockWiredTo'])
    }
  }
  if (labels.includes('IoModule')) {
    const ioModule = extractNode<PlantIoModule>(row['n'])
    if (!ioModule) throw new Error(`IoModule ${nodeId} could not be projected`)
    return {
      kind: 'IoModule',
      isStray: isStrayFor(row, 'IoModule'),
      ioModule,
      controller: extractNode<PlantDcsController>(row['ioModuleController']),
      channels: extractNodeList<PlantIoChannel>(row['ioModuleChannels']).sort(compareByNameThenId)
    }
  }
  if (labels.includes('IoChannel')) {
    const channel = extractNode<PlantIoChannel>(row['n'])
    if (!channel) throw new Error(`IoChannel ${nodeId} could not be projected`)
    return {
      kind: 'IoChannel',
      isStray: isStrayFor(row, 'IoChannel'),
      channel,
      ioModule: extractNode<PlantIoModule>(row['channelModule']),
      wiredFrom: extractNodeList<PlantFunctionBlock>(row['channelWiredFrom']).sort(
        compareByNameThenId
      )
    }
  }
  if (labels.includes('ControlTag')) {
    const tag = extractNode<PlantControlTag>(row['n'])
    if (!tag) throw new Error(`ControlTag ${nodeId} could not be projected`)
    return {
      kind: 'ControlTag',
      isStray: isStrayFor(row, 'ControlTag'),
      tag,
      module: extractNode<PlantControlModule>(row['tagModule']),
      alarms: extractNodeList<PlantControlAlarm>(row['tagAlarms']).sort(compareByNameThenId),
      setpoint: extractNode<PlantControlSetpoint>(row['tagSetpoint'])
    }
  }
  if (labels.includes('ControlAlarm')) {
    const alarm = extractNode<PlantControlAlarm>(row['n'])
    if (!alarm) throw new Error(`ControlAlarm ${nodeId} could not be projected`)
    return {
      kind: 'ControlAlarm',
      isStray: isStrayFor(row, 'ControlAlarm'),
      alarm,
      tag: extractNode<PlantControlTag>(row['alarmTag'])
    }
  }
  if (labels.includes('ControlSetpoint')) {
    const setpoint = extractNode<PlantControlSetpoint>(row['n'])
    if (!setpoint) throw new Error(`ControlSetpoint ${nodeId} could not be projected`)
    return {
      kind: 'ControlSetpoint',
      isStray: isStrayFor(row, 'ControlSetpoint'),
      setpoint,
      tag: extractNode<PlantControlTag>(row['setpointTag'])
    }
  }
  throw new Error(`DCS node ${nodeId} has unsupported labels ${labels.join(',')}`)
}

export type { PlantDcsNodeKind }
